import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { useCreateNodeWithEdges, useUpdateNode, useSubmitForReview } from '@/hooks/useNodes';
import { useSimpleUpdateNode, useSimpleSubmitForReview } from '@/hooks/useSimpleNodeOperations';
import { useDisciplines } from '@/hooks/useDisciplines';
import { useRoleAccess } from '@/hooks/useRoleAccess';
import { toast } from 'sonner';
import RichTextEditor from './RichTextEditor';
import NodeConnectionInterface from './NodeConnectionInterface';
import AttributionFields, { AttributionData } from "./AttributionFields";
import type { Tables } from '@/integrations/supabase/types';

type Node = Tables<'nodes'>;
type NodeType = 'article' | 'lesson' | 'concept' | 'reference';
type NodeStatus = 'draft' | 'in_review' | 'published' | 'archived';

interface NodeEdge {
  targetNodeId: string;
  relationshipType: 'prerequisite' | 'related' | 'deepens' | 'critiques' | 'applies' | 'exemplifies';
  weight?: number;
}

const advancedNodeSchema = z.object({
  title: z.string().min(1, 'Title is required').max(200, 'Title too long'),
  content: z.string().min(1, 'Content is required'),
  node_type: z.enum(['article', 'lesson', 'concept', 'reference']),
  discipline_id: z.string().min(1, 'Please select a discipline'),
  status: z.enum(['draft', 'in_review', 'published', 'archived']),
  tags: z.string().optional(),
  difficulty_level: z.number().min(1).max(5).default(2),
  estimated_study_time: z.number().min(1).max(300).default(15),
  content_type: z.enum(['foundational', 'informational', 'practical', 'assessment', 'project']).default('informational'),
  competency_tags: z.string().optional(),
  learning_objectives: z.string().optional(),
});

type AdvancedNodeFormData = z.infer<typeof advancedNodeSchema>;

interface AdvancedNodeEditorProps {
  node?: Node;
  onSave?: (node: Node) => void;
  onCancel?: () => void;
}

const ADV_ATTR_FN_URL = "https://dcalavwuedknvuofvttt.functions.supabase.co/detect-attribution-need";

const AdvancedNodeEditor: React.FC<AdvancedNodeEditorProps> = ({ node, onSave, onCancel }) => {
  const { data: disciplines } = useDisciplines();
  const createNodeWithEdgesMutation = useCreateNodeWithEdges();
  const updateNodeMutation = useUpdateNode();
  const submitForReviewMutation = useSubmitForReview();
  const simpleUpdateMutation = useSimpleUpdateNode();
  const simpleSubmitMutation = useSimpleSubmitForReview();
  const { canEditContent } = useRoleAccess();

  const [edges, setEdges] = useState<NodeEdge[]>([]);
  const [activeTab, setActiveTab] = useState('content');
  const [attributionCheck, setAttributionCheck] = useState<{
    required: boolean;
    reasons: string[];
  } | null>(null);
  const [attribution, setAttribution] = useState<AttributionData>({});

  const form = useForm<AdvancedNodeFormData>({
    resolver: zodResolver(advancedNodeSchema),
    defaultValues: {
      title: node?.title || '',
      content: typeof node?.content === 'object' && node.content && 'text' in node.content 
        ? String(node.content.text) 
        : '',
      node_type: (node?.node_type as NodeType) || 'article',
      discipline_id: node?.discipline_id || '',
      status: (node?.status as NodeStatus) || 'draft',
      tags: '',
      difficulty_level: node?.difficulty_level ?? 2,
      estimated_study_time: node?.estimated_study_time ?? 15,
      content_type: (node?.content_type as 'foundational' | 'informational' | 'practical' | 'assessment' | 'project') ?? 'informational',
      competency_tags: Array.isArray(node?.competency_tags) ? node.competency_tags.join(', ') : '',
      learning_objectives: Array.isArray(node?.learning_objectives) ? node.learning_objectives.join(', ') : '',
    },
  });

  // Check when content changes (debounced)
  React.useEffect(() => {
    const handler = setTimeout(async () => {
      const content = form.getValues("content");
      if (!content || content.length < 5) {
        setAttributionCheck(null);
        return;
      }
      // Call backend to check if requires attribution
      try {
        const resp = await fetch(ADV_ATTR_FN_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ content })
        });
        const result = await resp.json();
        setAttributionCheck(result);
      } catch (err) {
        setAttributionCheck(null);
      }
    }, 500);
    return () => clearTimeout(handler);
    // eslint-disable-next-line
  }, [form.watch("content")]);

  const isLoading = createNodeWithEdgesMutation.isPending || updateNodeMutation.isPending || submitForReviewMutation.isPending || simpleUpdateMutation.isPending || simpleSubmitMutation.isPending;

  // Main save handler: attribution only required for published content
  const handleSaveWithConnections = async (data: AdvancedNodeFormData) => {
    // If attribution required AND trying to publish -> block if missing
    if (attributionCheck?.required && data.status === 'published') {
      const hasCore =
        attribution.source_title &&
        attribution.source_author &&
        (attribution.source_url || attribution.source_publication || attribution.license_type);
      if (!hasCore) {
        toast.warning("Attribution required: You must fill in all required fields before you can publish this content. Save as draft to continue working.");
        setActiveTab("attribution");
        return;
      }
    }
    try {
      const tags = data.tags ? data.tags.split(",").map(tag => tag.trim()).filter(Boolean) : [];
      if (node) {
        // Update existing node using simple update to avoid constraints
        const updatedNode = await simpleUpdateMutation.mutateAsync({
          id: node.id,
          updates: {
            title: data.title,
            content: {
              text: data.content,
              preview: data.content.substring(0, 200) + (data.content.length > 200 ? '...' : ''),
            },
            node_type: data.node_type,
            discipline_id: data.discipline_id,
            status: data.status,
            // Include new metadata fields
            difficulty_level: data.difficulty_level,
            estimated_study_time: data.estimated_study_time,
            content_type: data.content_type,
            competency_tags: data.competency_tags ? data.competency_tags.split(',').map(tag => tag.trim()).filter(Boolean) : [],
            learning_objectives: data.learning_objectives ? data.learning_objectives.split(',').map(obj => obj.trim()).filter(Boolean) : [],
          },
        });
        onSave?.(updatedNode);
      } else {
        // Create new node with edges (metadata will be updated separately)
        const result = await createNodeWithEdgesMutation.mutateAsync({
          title: data.title,
          content: {
            text: data.content,
            preview: data.content.substring(0, 200) + (data.content.length > 200 ? '...' : ''),
          },
          nodeType: data.node_type,
          disciplineId: data.discipline_id,
          status: data.status,
          edges,
          tags,
        });
        
        // Update the created node with metadata fields
        if (result.node) {
          await simpleUpdateMutation.mutateAsync({
            id: result.node.id,
            updates: {
              difficulty_level: data.difficulty_level,
              estimated_study_time: data.estimated_study_time,
              content_type: data.content_type,
              competency_tags: data.competency_tags ? data.competency_tags.split(',').map(tag => tag.trim()).filter(Boolean) : [],
              learning_objectives: data.learning_objectives ? data.learning_objectives.split(',').map(obj => obj.trim()).filter(Boolean) : [],
            },
          });
        }
        toast.success(`Content created with ${edges.length} connections`);
        onSave?.(result.node);
      }
    } catch (error) {
      console.error('Error saving node:', error);
      toast.error('Failed to save content');
    }
  };

  const handleSubmitForReview = async (data: AdvancedNodeFormData) => {
    try {
      let nodeToSubmit = node;
      
      // If this is a new node, create it first as draft
      if (!nodeToSubmit) {
        const tags = data.tags ? data.tags.split(",").map(tag => tag.trim()).filter(Boolean) : [];
        const result = await createNodeWithEdgesMutation.mutateAsync({
          title: data.title,
          content: {
            text: data.content,
            preview: data.content.substring(0, 200) + (data.content.length > 200 ? '...' : ''),
          },
          nodeType: data.node_type,
          disciplineId: data.discipline_id,
          status: 'draft',
          edges,
          tags,
        });
        nodeToSubmit = result.node;
        
        // Update with metadata fields
        if (nodeToSubmit) {
          await simpleUpdateMutation.mutateAsync({
            id: nodeToSubmit.id,
            updates: {
              difficulty_level: data.difficulty_level,
              estimated_study_time: data.estimated_study_time,
              content_type: data.content_type,
              competency_tags: data.competency_tags ? data.competency_tags.split(',').map(tag => tag.trim()).filter(Boolean) : [],
              learning_objectives: data.learning_objectives ? data.learning_objectives.split(',').map(obj => obj.trim()).filter(Boolean) : [],
            },
          });
        }
      } else if (form.formState.isDirty) {
        // Save any changes first using simple update
        const updatedNode = await simpleUpdateMutation.mutateAsync({
          id: node.id,
          updates: {
            title: data.title,
            content: {
              text: data.content,
              preview: data.content.substring(0, 200) + (data.content.length > 200 ? '...' : ''),
            },
            node_type: data.node_type,
            discipline_id: data.discipline_id,
            status: 'draft', // Keep as draft before submitting
            // Include metadata fields
            difficulty_level: data.difficulty_level,
            estimated_study_time: data.estimated_study_time,
            content_type: data.content_type,
            competency_tags: data.competency_tags ? data.competency_tags.split(',').map(tag => tag.trim()).filter(Boolean) : [],
            learning_objectives: data.learning_objectives ? data.learning_objectives.split(',').map(obj => obj.trim()).filter(Boolean) : [],
          },
        });
        nodeToSubmit = updatedNode;
      }

      // Now submit for review using simple submit
      if (nodeToSubmit) {
        await simpleSubmitMutation.mutateAsync({ nodeId: nodeToSubmit.id });
        onSave?.(nodeToSubmit);
      }
    } catch (error) {
      console.error('Error submitting for review:', error);
      toast.error('Failed to submit content for review');
    }
  };

  return (
    <Card className="w-full max-w-5xl mx-auto">
      <CardHeader>
        <CardTitle>{node ? 'Edit Content' : 'Create New Content'}</CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form className="space-y-6">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="content">Content</TabsTrigger>
                <TabsTrigger value="connections">Connections</TabsTrigger>
                <TabsTrigger value="metadata">Metadata</TabsTrigger>
                <TabsTrigger value="attribution">
                  Attribution
                  {attributionCheck?.required && (
                    <span className="ml-2 text-xs text-black font-bold animate-pulse">Required</span>
                  )}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="content" className="space-y-4">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Title</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter content title..." {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="content"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Content</FormLabel>
                      <FormControl>
                        <RichTextEditor
                          content={field.value}
                          onChange={field.onChange}
                          placeholder="Start writing your content..."
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </TabsContent>

              <TabsContent value="connections">
                <NodeConnectionInterface edges={edges} onChange={setEdges} />
              </TabsContent>

              <TabsContent value="metadata" className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="node_type"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Content Type</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="article">Article</SelectItem>
                            <SelectItem value="lesson">Lesson</SelectItem>
                            <SelectItem value="concept">Concept</SelectItem>
                            <SelectItem value="reference">Reference</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="discipline_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Discipline</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select discipline" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {disciplines?.map((discipline) => (
                              <SelectItem key={discipline.id} value={discipline.id}>
                                {discipline.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {canEditContent && (
                  <FormField
                    control={form.control}
                    name="status"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Status</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select status" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="draft">Draft</SelectItem>
                            <SelectItem value="in_review">In Review</SelectItem>
                            <SelectItem value="published">Published</SelectItem>
                            <SelectItem value="archived">Archived</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                <FormField
                  control={form.control}
                  name="tags"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tags</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="Enter tags separated by commas..."
                          {...field}
                        />
                      </FormControl>
                      <p className="text-xs text-black">
                        Separate multiple tags with commas
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Learning Metadata Fields */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <FormField
                    control={form.control}
                    name="difficulty_level"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Difficulty Level</FormLabel>
                        <Select onValueChange={(value) => field.onChange(parseInt(value))} defaultValue={field.value?.toString()}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select difficulty" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="1">1 - Foundational</SelectItem>
                            <SelectItem value="2">2 - Basic</SelectItem>
                            <SelectItem value="3">3 - Intermediate</SelectItem>
                            <SelectItem value="4">4 - Advanced</SelectItem>
                            <SelectItem value="5">5 - Research Level</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="estimated_study_time"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Study Time (minutes)</FormLabel>
                        <FormControl>
                          <Input 
                            type="number"
                            min="1"
                            max="300"
                            placeholder="15"
                            {...field}
                            onChange={(e) => field.onChange(parseInt(e.target.value) || 15)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="content_type"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Learning Type</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="foundational">Foundational</SelectItem>
                            <SelectItem value="informational">Informational</SelectItem>
                            <SelectItem value="practical">Practical</SelectItem>
                            <SelectItem value="assessment">Assessment</SelectItem>
                            <SelectItem value="project">Project</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="competency_tags"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Competency Tags</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="critical_thinking, problem_solving, analysis..."
                          {...field}
                        />
                      </FormControl>
                      <p className="text-xs text-black">
                        Skills and competencies this content develops (comma-separated)
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="learning_objectives"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Learning Objectives</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder="Understand key concepts, Apply theoretical knowledge, Develop practical skills..."
                          className="min-h-[80px]"
                          {...field}
                        />
                      </FormControl>
                      <p className="text-xs text-black">
                        What learners will achieve after studying this content (comma-separated)
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </TabsContent>

              <TabsContent value="attribution" className="space-y-4">
                {attributionCheck === null ? (
                  <div className="text-black text-sm">
                    No attribution needed for this content.
                  </div>
                ) : attributionCheck.required ? (
                  <div>
                    <div className="text-black text-sm font-semibold mb-2">
                      Attribution is <span className="underline font-bold">required</span> for this content:
                      <ul className="list-disc ml-6">
                        {attributionCheck.reasons.map((r, i) => (
                          <li key={i}>{r}</li>
                        ))}
                      </ul>
                      <span className="block text-xs text-black mt-2">You must provide source/author/license info to publish.</span>
                    </div>
                    <AttributionFields value={attribution} onChange={setAttribution} />
                  </div>
                ) : (
                  <div className="text-green-700 text-sm">
                    No detected citations—you may optionally provide attribution if needed.
                  </div>
                )}
              </TabsContent>
            </Tabs>

            <div className="flex gap-2 pt-4 border-t">
              <Button
                type="button"
                onClick={form.handleSubmit(handleSaveWithConnections)}
                disabled={isLoading}
              >
                {isLoading ? 'Saving...' : node ? 'Update Content' : 'Create Content'}
              </Button>
              
              <Button
                type="button"
                variant="secondary"
                onClick={form.handleSubmit(handleSubmitForReview)}
                disabled={isLoading || (!node && !form.formState.isValid)}
              >
                Submit for Review
              </Button>
              
              {onCancel && (
                <Button type="button" variant="outline" onClick={onCancel}>
                  Cancel
                </Button>
              )}
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default AdvancedNodeEditor;
